import { formatMoney } from '@/lib/money';

/** A single per-currency total — what each "EUR 12,000" / "USD 30,000" chip renders.
 *
 *  Money in this app is a value object (F4): each row has its own `currency` and a
 *  base USD snapshot only on entities that captured the FX rate at transaction time
 *  (Proposals, Invoices, etc.). For entities without a base snapshot (e.g. Deals),
 *  we must NEVER recompute FX client-side — instead group by `currency` and emit a
 *  separate total per group. The caller decides which it has.
 */
export interface CurrencyTotal {
  currency: string;
  amount: number;
}

type Row = {
  amount: string | number | null | undefined;
  currency: string | null | undefined;
};

/** Sum `rows` grouped by their `currency`. Rows with `null`/missing amount are
 *  ignored (they are either unset or masked server-side — F3). Returns an array
 *  sorted by descending total so the biggest currency leads. */
export function totalsByCurrency<R extends Row>(rows: readonly R[]): CurrencyTotal[] {
  const byCurrency = new Map<string, number>();
  for (const row of rows) {
    if (row.amount == null || row.currency == null) continue;
    const n = typeof row.amount === 'string' ? Number(row.amount) : row.amount;
    if (!Number.isFinite(n)) continue;
    byCurrency.set(row.currency, (byCurrency.get(row.currency) ?? 0) + n);
  }
  return [...byCurrency.entries()]
    .map(([currency, amount]) => ({ currency, amount }))
    .sort((a, b) => b.amount - a.amount);
}

/** Render an array of CurrencyTotal as a human string ("$42,000 · €18,000"). */
export function formatCurrencyTotals(totals: readonly CurrencyTotal[]): string {
  if (totals.length === 0) return '—';
  return totals
    .map((t) => formatMoney(t.amount, t.currency) ?? `${t.amount} ${t.currency}`)
    .join(' · ');
}

/** Sum a USD base column on entities that DO snapshot the base (Proposals, Invoices). */
export function sumBase<R extends { base_total?: string | null; base_amount?: string | null }>(
  rows: readonly R[],
  field: 'base_total' | 'base_amount' = 'base_total',
): number {
  let acc = 0;
  for (const row of rows) {
    const raw = row[field];
    const n = raw == null ? NaN : Number(raw);
    if (Number.isFinite(n)) acc += n;
  }
  return acc;
}
